import React, { useState, useEffect } from 'react';
import { arztService } from '../../services/arzt.service';
import { praxisService } from '../../services/praxis.service';
import { zuweisungService } from '../../services/zuweisung.service';
import { abwesenheitService } from '../../services/abwesenheit.service';
import { Arzt, Praxis, Zuweisung, Zeitslot, Abwesenheit } from '../../types';
import ConflictAlert from './ConflictAlert';

interface ZuweisungFormProps {
  zuweisung?: Zuweisung | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  arztId: string;
  praxisId: string;
  startDatum: string;
  endDatum: string;
  zeitslots: Zeitslot[];
  selectedDays: number[]; // 0=Sunday, 1=Monday, ...
  notizen: string;
}

interface FormErrors {
  arztId?: string;
  praxisId?: string;
  startDatum?: string;
  endDatum?: string;
  zeitslots?: string;
  selectedDays?: string;
}

const ZuweisungForm: React.FC<ZuweisungFormProps> = ({ zuweisung, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState<FormData>({
    arztId: '',
    praxisId: '',
    startDatum: '',
    endDatum: '',
    zeitslots: [],
    selectedDays: [1, 2, 3, 4, 5, 6], // Default: Mon-Sat
    notizen: '',
  });

  const [aerzte, setAerzte] = useState<Arzt[]>([]);
  const [praxen, setPraxen] = useState<Praxis[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [conflictingZuweisungen, setConflictingZuweisungen] = useState<Zuweisung[]>([]);
  const [abwesenheiten, setAbwesenheiten] = useState<Abwesenheit[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (zuweisung && aerzte.length > 0 && praxen.length > 0) {
      const datum = new Date(zuweisung.datum).toISOString().split('T')[0];
      setFormData({
        arztId: zuweisung.arztId.toString(),
        praxisId: zuweisung.praxisId.toString(),
        startDatum: datum,
        endDatum: datum,
        zeitslots: [zuweisung.zeitslot],
        selectedDays: [1, 2, 3, 4, 5, 6],
        notizen: zuweisung.notizen || '',
      });
    }
  }, [zuweisung, aerzte, praxen]);

  useEffect(() => {
    const fetchAbsences = async () => {
      if (formData.startDatum && formData.endDatum) {
        try {
          const start = new Date(formData.startDatum);
          const end = new Date(formData.endDatum);
          const data = await abwesenheitService.getAllAbwesenheiten({
            startDatum: start,
            endDatum: end
          });
          setAbwesenheiten(data);
        } catch (err) {
          console.error("Failed to fetch absences", err);
        }
      }
    };
    fetchAbsences();
  }, [formData.startDatum, formData.endDatum]);

  const loadData = async () => {
    try {
      setLoadingData(true);
      const [aerzteData, praxenData] = await Promise.all([
        arztService.getAllAerzte(),
        praxisService.getAllPraxen(),
      ]);
      setAerzte(aerzteData);
      setPraxen(praxenData);
    } catch (err: any) {
      setErrorMessage('Fehler beim Laden der Daten');
    } finally {
      setLoadingData(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.arztId) {
      newErrors.arztId = 'Bitte wählen Sie einen Arzt';
    }

    if (!formData.praxisId) {
      newErrors.praxisId = 'Bitte wählen Sie eine Praxis';
    }

    if (!formData.startDatum) {
      newErrors.startDatum = 'Bitte wählen Sie ein Startdatum';
    }

    if (!formData.endDatum) {
      newErrors.endDatum = 'Bitte wählen Sie ein Enddatum';
    }

    const minDate = new Date('2025-01-01');
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 2);

    if (formData.startDatum) {
      const start = new Date(formData.startDatum);
      if (start < minDate) {
        newErrors.startDatum = 'Oye! 2025 to pehla da time nai chalda. Purane zamaane ch na ja!';
      } else if (start > maxDate) {
        newErrors.startDatum = 'Oye bas kar! 2 saal to agge di planning? Aaram naal chalo!';
      }
    }

    if (formData.endDatum) {
      const end = new Date(formData.endDatum);
      if (end < minDate) {
        newErrors.endDatum = 'Ki kar riya hai? 2025 ton baad di tarik pao!';
      } else if (end > maxDate) {
        newErrors.endDatum = 'Thand rakh mitra! Eni dur di sochi janda? 2 saal limit we! Asad naal Gall karlo je Ena agge da sochna we.';
      }
    }

    if (formData.startDatum && formData.endDatum && formData.startDatum > formData.endDatum) {
      newErrors.endDatum = 'Enddatum muss nach dem Startdatum liegen';
    }

    if (formData.zeitslots.length === 0) {
      newErrors.zeitslots = 'Bitte wählen Sie mindestens einen Zeitslot';
    }

    if (!zuweisung && formData.selectedDays.length === 0) {
      newErrors.selectedDays = 'Bitte wählen Sie mindestens einen Wochentag';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getAvailableDayIndices = (arztId: string, praxisId: string): number[] => {
    if (!arztId || !praxisId) return [1, 2, 3, 4, 5, 6];

    const arztIdInt = parseInt(arztId);
    const praxisIdInt = parseInt(praxisId);
    const arzt = aerzte.find(a => a.id === arztIdInt);

    if (!arzt) return [1, 2, 3, 4, 5, 6];

    const dayMap: { [key: string]: number } = {
      'Sonntag': 0, 'Montag': 1, 'Dienstag': 2, 'Mittwoch': 3,
      'Donnerstag': 4, 'Freitag': 5, 'Samstag': 6
    };

    let availableDays: string[] = [];
    if (arzt.praxisAssociations && arzt.praxisAssociations.length > 0) {
      const assoc = arzt.praxisAssociations.find(a => a.praxisId === praxisIdInt);
      if (assoc?.arbeitstage) availableDays = assoc.arbeitstage;
    } else if (arzt.arbeitstage) {
      availableDays = arzt.arbeitstage;
    }

    if (availableDays.length === 0) return [1, 2, 3, 4, 5, 6]; // Default if nothing specific

    return availableDays.map(dayName => dayMap[dayName]).filter(d => d !== undefined);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      // Auto-update selected days when Doctor or Practice changes
      if (name === 'arztId' || name === 'praxisId') {
        const days = getAvailableDayIndices(newData.arztId, newData.praxisId);
        // Only update selectedDays if the new combination yields a restricted set
        // or if we want to enforce the available days by default
        newData.selectedDays = days;
      }
      return newData;
    });

    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
    // Clear conflict when form changes
    if (conflictingZuweisungen.length > 0) {
      setConflictingZuweisungen([]);
    }
  };

  const handleZeitslotChange = (zeitslot: Zeitslot) => {
    setFormData(prev => {
      const zeitslots = prev.zeitslots.includes(zeitslot)
        ? prev.zeitslots.filter(z => z !== zeitslot)
        : [...prev.zeitslots, zeitslot];
      return { ...prev, zeitslots };
    });
    if (errors.zeitslots) {
      setErrors(prev => ({ ...prev, zeitslots: undefined }));
    }
    // Clear conflict when form changes
    if (conflictingZuweisungen.length > 0) {
      setConflictingZuweisungen([]);
    }
  };

  const handleDayChange = (dayIndex: number) => {
    setFormData(prev => {
      const selectedDays = prev.selectedDays.includes(dayIndex)
        ? prev.selectedDays.filter(d => d !== dayIndex)
        : [...prev.selectedDays, dayIndex];
      return { ...prev, selectedDays };
    });
  };

  const handleAllDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setFormData(prev => ({ ...prev, selectedDays: [1, 2, 3, 4, 5, 6] }));
    } else {
      setFormData(prev => ({ ...prev, selectedDays: [] }));
    }
  };

  const checkConflict = async (): Promise<boolean> => {
    // For editing existing assignment, use single date check
    if (zuweisung && formData.startDatum && formData.zeitslots.length > 0) {
      try {
        const conflictData = {
          arztId: parseInt(formData.arztId),
          praxisId: parseInt(formData.praxisId),
          datum: new Date(formData.startDatum),
          zeitslot: formData.zeitslots[0],
          id: zuweisung.id,
        };

        const result = await zuweisungService.checkConflict(conflictData);

        if (result.hasConflict) {
          const conflicts = (result as any).conflictingAssignments || result.conflictingZuweisungen || [];
          const formattedConflicts: Zuweisung[] = conflicts.map((c: any) => ({
            id: c.id,
            arztId: parseInt(formData.arztId),
            praxisId: 0,
            datum: new Date(c.datum),
            zeitslot: c.zeitslot,
            notizen: '',
            createdAt: new Date(),
            updatedAt: new Date(),
            praxis: { name: c.praxisName } as Praxis,
          }));

          setConflictingZuweisungen(formattedConflicts);
          return true;
        }
      } catch (err: any) {
        console.error('Error checking conflict:', err);
      }
    }

    setConflictingZuweisungen([]);
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);
    setConflictingZuweisungen([]);

    if (!validateForm()) {
      return;
    }

    // Check for conflicts before submission (only for edit mode)
    if (zuweisung) {
      const hasConflict = await checkConflict();
      if (hasConflict) {
        setErrorMessage('Zeitkonflikt erkannt. Bitte beheben Sie den Konflikt vor dem Speichern.');
        return;
      }
    }

    try {
      setLoading(true);

      if (zuweisung) {
        // Edit mode: update single assignment
        const zuweisungData = {
          arztId: parseInt(formData.arztId),
          praxisId: parseInt(formData.praxisId),
          datum: new Date(formData.startDatum),
          zeitslot: formData.zeitslots[0] as Zeitslot,
          notizen: formData.notizen.trim() || undefined,
        };
        await zuweisungService.updateZuweisung(zuweisung.id, zuweisungData);
        setSuccessMessage('Zuweisung erfolgreich aktualisiert');
      } else {
        // Create mode: create multiple assignments for date range and time slots
        const startDate = new Date(formData.startDatum);
        const endDate = new Date(formData.endDatum);
        const assignments = [];

        // Generate all dates in range
        for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
          // Check if day matches selected days
          const day = date.getDay();
          const isSelected = formData.selectedDays.includes(day);

          if (!isSelected) {
            continue;
          }

          // Create assignment for each selected time slot
          for (const zeitslot of formData.zeitslots) {
            assignments.push({
              arztId: parseInt(formData.arztId),
              praxisId: parseInt(formData.praxisId),
              datum: new Date(date),
              zeitslot: zeitslot as Zeitslot,
              notizen: formData.notizen.trim() || undefined,
            });
          }
        }

        // Create all assignments
        let successCount = 0;
        let conflictCount = 0;

        for (const assignment of assignments) {
          try {
            await zuweisungService.createZuweisung(assignment);
            successCount++;
          } catch (err: any) {
            if (err.response?.status === 409) {
              conflictCount++;
            } else {
              throw err;
            }
          }
        }

        if (successCount > 0) {
          setSuccessMessage(`${successCount} Zuweisung(en) erfolgreich erstellt${conflictCount > 0 ? ` (${conflictCount} übersprungen wegen Konflikten)` : ''}`);
        } else if (conflictCount > 0) {
          setErrorMessage('Alle Zuweisungen wurden übersprungen wegen Zeitkonflikten.');
          return;
        }
      }

      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err: any) {
      if (err.response?.status === 409) {
        const conflictError = err.response?.data;

        if (conflictError?.error === 'HOLIDAY_CONFLICT') {
          setErrorMessage(`Arzt ist im Urlaub/Abwesend: ${conflictError.message}`);
          // Do not set conflictingZuweisungen as structure differs
          return;
        }

        if (conflictError?.conflictingAssignments) {
          const conflicts: Zuweisung[] = conflictError.conflictingAssignments.map((ca: any) => ({
            id: ca.id,
            arztId: parseInt(formData.arztId),
            praxisId: 0,
            datum: new Date(ca.datum),
            zeitslot: ca.zeitslot,
            notizen: '',
            createdAt: new Date(),
            updatedAt: new Date(),
            praxis: { name: ca.praxisName } as Praxis,
          }));
          setConflictingZuweisungen(conflicts);
        }
        setErrorMessage('Zeitkonflikt: Der Arzt ist bereits zur gleichen Zeit an einer anderen Praxis eingeplant.');
      } else {
        setErrorMessage(err.response?.data?.message || 'Fehler beim Speichern der Zuweisung');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Lade Daten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">
        {zuweisung ? 'Zuweisung bearbeiten' : 'Neue Zuweisung'}
      </h2>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {errorMessage}
        </div>
      )}

      <ConflictAlert conflictingZuweisungen={conflictingZuweisungen} />

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="arztId" className="block text-sm font-medium text-gray-700 mb-1">
              Arzt <span className="text-red-500">*</span>
            </label>
            <select
              id="arztId"
              name="arztId"
              value={formData.arztId}
              onChange={handleChange}
              className={`w-full px-3 py-3 min-h-[44px] text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.arztId ? 'border-red-500' : 'border-gray-300'
                }`}
            >
              <option value="">-- Arzt auswählen --</option>
              {aerzte.filter(arzt => {
                // Debug logging
                // console.log(`Checking Arzt: ${arzt.nachname}, PraxisFilter: ${formData.praxisId}`);

                // 1. Filter by Praxis association
                if (formData.praxisId) {
                  const praxisIdInt = parseInt(formData.praxisId);

                  if (arzt.praxisAssociations) {
                    // Modern Logic: Check specific associations
                    const association = arzt.praxisAssociations.find(a => a.praxisId === praxisIdInt);

                    // if (arzt.nachname === 'Rizwan') {
                    //  console.log('Rizwan Check:', { associations: arzt.praxisAssociations, target: praxisIdInt, found: !!association });
                    // }

                    if (!association) return false;

                    // Check workday availability (only for single day selection)
                    if (formData.startDatum === formData.endDatum && formData.startDatum !== '') {
                      const date = new Date(formData.startDatum);
                      const dayName = date.toLocaleDateString('de-DE', { weekday: 'long' });
                      if (!association.arbeitstage?.includes(dayName)) {
                        return false;
                      }
                    }
                  } else if (arzt.praxen) {
                    // Legacy Logic: Check legacy relation
                    const hasLegacyAssociation = arzt.praxen.some(p => p.id === praxisIdInt);
                    if (!hasLegacyAssociation) return false;

                    // Legacy workday availability
                    if (formData.startDatum === formData.endDatum && formData.startDatum !== '') {
                      const date = new Date(formData.startDatum);
                      const dayName = date.toLocaleDateString('de-DE', { weekday: 'long' });
                      if (arzt.arbeitstage && !arzt.arbeitstage.includes(dayName)) {
                        return false;
                      }
                    }
                  } else {
                    // No availability data at all -> hide
                    return false;
                  }
                }

                // 2. Absence check
                if (formData.startDatum === formData.endDatum && formData.startDatum !== '') {
                  const date = new Date(formData.startDatum);
                  const isAbsent = abwesenheiten.some(abw =>
                    abw.arztId === arzt.id &&
                    new Date(abw.startDatum) <= date &&
                    new Date(abw.endDatum) >= date
                  );
                  if (isAbsent) return false;
                }

                return true;
              }).map((arzt) => (
                <option key={arzt.id} value={arzt.id}>
                  {arzt.vorname} {arzt.nachname}
                  {arzt.qualifikation ? ` (${arzt.qualifikation})` : ''}
                </option>
              ))}
            </select>
            {errors.arztId && (
              <p className="text-red-500 text-sm mt-1">{errors.arztId}</p>
            )}
          </div>



          <div>
            <label htmlFor="praxisId" className="block text-sm font-medium text-gray-700 mb-1">
              Praxis <span className="text-red-500">*</span>
            </label>
            <select
              id="praxisId"
              name="praxisId"
              value={formData.praxisId}
              onChange={handleChange}
              className={`w-full px-3 py-3 min-h-[44px] text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.praxisId ? 'border-red-500' : 'border-gray-300'
                }`}
            >
              <option value="">-- Praxis auswählen --</option>
              {praxen
                .filter(praxis => {
                  if (!formData.arztId) return true;
                  const arztIdInt = parseInt(formData.arztId);
                  const arzt = aerzte.find(a => a.id === arztIdInt);
                  if (!arzt) return true;

                  // Check modern associations
                  if (arzt.praxisAssociations && arzt.praxisAssociations.length > 0) {
                    return arzt.praxisAssociations.some(a => a.praxisId === praxis.id);
                  }

                  // Check legacy associations
                  if (arzt.praxen && arzt.praxen.length > 0) {
                    return arzt.praxen.some(p => p.id === praxis.id);
                  }

                  return false; // If doctor has no associations, show no practices? Or all? Let's strict: none.
                })
                .map((praxis) => (
                  <option key={praxis.id} value={praxis.id}>
                    {praxis.name}
                    {praxis.stadt ? ` - ${praxis.stadt}` : ''}
                  </option>
                ))}
            </select>
            {errors.praxisId && (
              <p className="text-red-500 text-sm mt-1">{errors.praxisId}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="startDatum" className="block text-sm font-medium text-gray-700 mb-1">
              {zuweisung ? 'Datum' : 'Von Datum'} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="startDatum"
              name="startDatum"
              value={formData.startDatum}
              onChange={handleChange}
              className={`w-full px-3 py-3 min-h-[44px] text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.startDatum ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.startDatum && (
              <p className="text-red-500 text-sm mt-1">{errors.startDatum}</p>
            )}
          </div>

          {!zuweisung && (
            <div>
              <label htmlFor="endDatum" className="block text-sm font-medium text-gray-700 mb-1">
                Bis Datum <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="endDatum"
                name="endDatum"
                value={formData.endDatum}
                onChange={handleChange}
                min={formData.startDatum}
                className={`w-full px-3 py-3 min-h-[44px] text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.endDatum ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {errors.endDatum && (
                <p className="text-red-500 text-sm mt-1">{errors.endDatum}</p>
              )}
            </div>
          )}
        </div>

        {!zuweisung && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Wochentage <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {[
                { day: 1, label: 'Mo' },
                { day: 2, label: 'Di' },
                { day: 3, label: 'Mi' },
                { day: 4, label: 'Do' },
                { day: 5, label: 'Fr' },
                { day: 6, label: 'Sa' },
              ].filter(({ day, label }) => {
                // Determine available days
                if (!formData.arztId || !formData.praxisId) return true;

                const arztIdInt = parseInt(formData.arztId);
                const praxisIdInt = parseInt(formData.praxisId);
                const arzt = aerzte.find(a => a.id === arztIdInt);

                if (!arzt) return true;

                const dayMap: { [key: string]: number } = {
                  'Sonntag': 0, 'Montag': 1, 'Dienstag': 2, 'Mittwoch': 3,
                  'Donnerstag': 4, 'Freitag': 5, 'Samstag': 6
                };

                let availableDays: string[] = [];
                // Check modern associations
                if (arzt.praxisAssociations && arzt.praxisAssociations.length > 0) {
                  const assoc = arzt.praxisAssociations.find(a => a.praxisId === praxisIdInt);
                  if (assoc?.arbeitstage) availableDays = assoc.arbeitstage;
                } else if (arzt.arbeitstage) {
                  // Legacy fallback
                  availableDays = arzt.arbeitstage;
                }

                // If no days defined, maybe show none? Or all? 
                // Let's assume if array is empty but association exists, they work 0 days.
                if (availableDays.length === 0) return false;

                // Reverse lookup the label (e.g. "Mo" needs to match "Montag")
                // Actually my dayMap keys map to day Index.
                // The availableDays array contains full German strings "Montag".

                // Check if the current checkbox day index corresponds to any of the availableDays strings
                // e.g. day=1 (Mon) matches if availableDays includes "Montag"

                const dayName = Object.keys(dayMap).find(key => dayMap[key] === day);
                return dayName ? availableDays.includes(dayName) : false;
              }).map(({ day, label }) => (
                <label key={day} className="inline-flex items-center bg-gray-50 px-3 py-2 rounded border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.selectedDays.includes(day)}
                    onChange={() => handleDayChange(day)}
                    className="form-checkbox h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">{label}</span>
                </label>
              ))}
              <label className="inline-flex items-center px-3 py-2 cursor-pointer text-sm text-blue-600 hover:text-blue-800">
                <input
                  type="checkbox"
                  checked={formData.selectedDays.length === 6}
                  onChange={handleAllDaysChange}
                  className="mr-2"
                />
                Alle
              </label>
            </div>
            {/* Error display for selectedDays if you added it to FormErrors interface, 
                otherwise just implied by validation */}
            {(errors as any).selectedDays && (
              <p className="text-red-500 text-sm mt-1">{(errors as any).selectedDays}</p>
            )}
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Zeitslot(s) <span className="text-red-500">*</span>
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.zeitslots.includes(Zeitslot.VORMITTAG)}
                onChange={() => handleZeitslotChange(Zeitslot.VORMITTAG)}
                className="form-checkbox h-5 w-5 text-blue-600 focus:ring-blue-500 rounded"
              />
              <span className="ml-3">Vormittag (07:30 - 13:00)</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.zeitslots.includes(Zeitslot.NACHMITTAG)}
                onChange={() => handleZeitslotChange(Zeitslot.NACHMITTAG)}
                className="form-checkbox h-5 w-5 text-blue-600 focus:ring-blue-500 rounded"
              />
              <span className="ml-3">Nachmittag (14:00 - 18:30)</span>
            </label>
          </div>
          {errors.zeitslots && (
            <p className="text-red-500 text-sm mt-1">{errors.zeitslots}</p>
          )}
          {!zuweisung && formData.zeitslots.length > 0 && (
            <p className="text-sm text-gray-600 mt-2">
              {formData.zeitslots.length === 2 ? 'Beide Zeitslots ausgewählt' : `${formData.zeitslots.length} Zeitslot ausgewählt`}
            </p>
          )}
        </div>

        {
          !zuweisung && formData.startDatum && formData.endDatum && formData.zeitslots.length > 0 && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800">
                <strong>Hinweis:</strong> Es werden{' '}
                {(() => {
                  const start = new Date(formData.startDatum);
                  const end = new Date(formData.endDatum);

                  let dayCount = 0;
                  let currentDate = new Date(start);

                  while (currentDate <= end) {
                    if (formData.selectedDays.includes(currentDate.getDay())) {
                      dayCount++;
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                  }

                  const total = dayCount * formData.zeitslots.length;
                  return `${total} Zuweisung${total > 1 ? 'en' : ''} erstellt (${dayCount} Tag${dayCount > 1 ? 'e' : ''} × ${formData.zeitslots.length} Zeitslot${formData.zeitslots.length > 1 ? 's' : ''})`;
                })()}
              </p>
            </div>
          )
        }

        <div className="mb-6">
          <label htmlFor="notizen" className="block text-sm font-medium text-gray-700 mb-1">
            Notizen (optional)
          </label>
          <textarea
            id="notizen"
            name="notizen"
            value={formData.notizen}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Zusätzliche Informationen zur Zuweisung..."
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-3 min-h-[44px] border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || conflictingZuweisungen.length > 0}
            className="px-4 py-3 min-h-[44px] bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Speichert...' : zuweisung ? 'Aktualisieren' : 'Erstellen'}
          </button>
        </div>

      </form >
    </div >
  );
};

export default ZuweisungForm;
